import { BaseSelect, SelectProps } from '@app/components';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PaymentType, getPaymentTypeFR } from '@app/common';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

const columnHelper = createColumnHelper<PaymentType>();

export const PaymentTypeSelect = ({ label = 'Type de paiement', name, ...props }: Omit<SelectProps<any>, 'table'>) => {
  const { control, setValue } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const { isMultiple } = props;
  
  const value = useWatch({
    control,
    name: name!,
    defaultValue: isMultiple ? [] : undefined
  });

  const selectedValues: PaymentType[] = isMultiple 
    ? (value || []) 
    : (value ? [value] : []);

  const columns = [
    columnHelper.display({
      id: 'payment-type',
      header: () => (
        <BaseSelect.ListHeaderItem width="100%">
          Nom
        </BaseSelect.ListHeaderItem>
      ),
      cell: (info) => {
        return <BaseSelect.ListItemCol width="100%">
          <BaseSelect.ListItemText numberOfLines={1} bold>
            {getPaymentTypeFR(info.row.original)}
          </BaseSelect.ListItemText>
        </BaseSelect.ListItemCol>
      },
    }),
  ];

  const table = useReactTable({
    data: Object.values(PaymentType) as PaymentType[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onSelect = (item: PaymentType) => {
    if (isMultiple) {
      if (!selectedValues.includes(item)) {
        setValue(name!, [...selectedValues, item], { shouldDirty: true });
      }
    } else {
      setValue(name!, item, { shouldDirty: true });
      setIsOpen(false);
    }
  };

  const onRemove = (item: PaymentType) => {
    if (isMultiple) {
      setValue(name!, selectedValues.filter(v => v !== item), { shouldDirty: true });
    } else {
      setValue(name!, undefined, { shouldDirty: true });
    }
  };

  const isActive = (item: PaymentType) => selectedValues.includes(item);

  const formattedLabel = () => {
    if (!selectedValues.length) {
      return label!;
    }
    
    const labels = selectedValues.map(v => getPaymentTypeFR(v));
    let temp = label + ': ' + labels.join(', ').substring(0, 33);
    return temp.length >= 33 ? temp + '...' : temp;
  };

  return (
    <BaseSelect
      name={name}
      label={label}
      table={table}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      values={selectedValues}
      onSelect={onSelect}
      onRemove={onRemove}
      isActive={isActive}
      formattedLabel={formattedLabel()}
      render={(item) => (
        <BaseSelect.ListItem key={item as string} compact isActive onPress={() => onRemove(item as PaymentType)}>
          <BaseSelect.ListItemCol width='100%'>
            <BaseSelect.ListItemText numberOfLines={1} bold>
              {getPaymentTypeFR(item as PaymentType)}
            </BaseSelect.ListItemText>
          </BaseSelect.ListItemCol>
        </BaseSelect.ListItem>
      )}
      {...props} 
    />
  );
};
